<template>
  <div class="auth-page theme-page">
    <div class="content-section auth-layout">
      <div class="container">
        <div class="auth-center">
          <div class="row justify-content-center">
            <div class="col-lg-5 col-md-7 col-sm-9 col-12 auth-card theme-panel">
              <div class="auth-brand">
                <div class="brand-mark" aria-hidden="true">
                  <span class="brand-bracket">&#91;</span>
                  <span class="brand-glyph"></span>
                  <span class="brand-bracket">&#93;</span>
                </div>
                <span class="brand-name">Abol Verify</span>
              </div>

              <!-- Success -->
              <div v-if="done" class="state-block text-center">
                <h5 class="title auth-title">
                  {{ doneMode === "phone" ? "Password reset" : "Check your inbox" }}
                </h5>
                <h6 class="sub-title theme-subtitle auth-copy">
                  <template v-if="doneMode === 'phone'">
                    Your password has been reset successfully. You can now log in
                    with your new password.
                  </template>
                  <template v-else>
                    If an account exists for <strong>{{ submittedEmail }}</strong>,
                    we've sent a link to reset your password. The link expires
                    shortly.
                  </template>
                </h6>
                <router-link :to="{ name: LOGIN_ROUTE }" class="btn btn-dark mt-3">
                  Back to login
                </router-link>
              </div>

              <template v-else>
                <h5 class="text-center title auth-title">{{ headerTitle }}</h5>
                <h6 class="sub-title theme-subtitle auth-copy text-center">
                  {{ headerSubtitle }}
                </h6>
                <p v-if="errorMessage" class="text-center text-danger mt-2">
                  {{ errorMessage }}
                </p>

                <!-- Method selector. Always shown; the phone path surfaces a
                     clear message until the Firebase env values are filled in. -->
                <div
                  class="auth-toggle"
                  role="tablist"
                  aria-label="Reset method"
                >
                  <button
                    type="button"
                    role="tab"
                    :aria-selected="method === 'email'"
                    :class="{ 'is-active': method === 'email' }"
                    v-on:click="setMethod('email')"
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    role="tab"
                    :aria-selected="method === 'phone'"
                    :class="{ 'is-active': method === 'phone' }"
                    v-on:click="setMethod('phone')"
                  >
                    Phone
                  </button>
                </div>

                <!-- EMAIL: request reset link -->
                <Form v-if="method === 'email'" class="form" @submit="submitEmail">
                  <div class="form-group">
                    <Field
                      id="email"
                      v-model="forgotObj.email"
                      :rules="{ required: ['Email'], email: ['Email'] }"
                      type="email"
                      name="email"
                      class="form-control shadow-none"
                      placeholder="Email"
                      autocomplete="email"
                    />
                    <ErrorMessage name="email" class="form-text text-danger" />
                  </div>
                  <button type="submit" class="btn btn-dark" :disabled="isLoading">
                    <span
                      v-if="isLoading"
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span v-if="!isLoading">Send reset link</span>
                  </button>
                  <p class="auth-switch-link">
                    Remembered your password?
                    <router-link :to="{ name: LOGIN_ROUTE }">Login</router-link>
                  </p>
                </Form>

                <!-- PHONE: OTP-verified reset -->
                <template v-else>
                  <!-- Step 1: enter phone, send code -->
                  <Form v-if="phoneStep === 'request'" class="form" @submit="sendOtp">
                    <div class="form-group">
                      <div class="phone-input-row">
                        <select
                          v-model="selectedCountryCode"
                          class="form-control shadow-none country-code-select"
                          aria-label="Country code"
                        >
                          <option
                            v-for="country in countryCodes"
                            :key="country.iso"
                            :value="country.code"
                          >
                            {{ country.label }}
                          </option>
                        </select>
                        <input
                          v-model.trim="phoneLocalNumber"
                          type="tel"
                          class="form-control shadow-none"
                          placeholder="Phone number"
                          autocomplete="tel-national"
                          v-on:input="onPhoneInput"
                        />
                      </div>
                      <span v-if="phoneError" class="form-text text-danger">{{ phoneError }}</span>
                    </div>
                    <button type="submit" class="btn btn-dark" :disabled="isLoading">
                      <span
                        v-if="isLoading"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span v-if="!isLoading">Send code</span>
                    </button>
                    <p class="auth-switch-link">
                      Remembered your password?
                      <router-link :to="{ name: LOGIN_ROUTE }">Login</router-link>
                    </p>
                  </Form>

                  <!-- Step 2: enter the OTP -->
                  <Form v-else-if="phoneStep === 'verify'" class="form" @submit="verifyOtp">
                    <div class="form-group">
                      <input
                        v-model.trim="otpCode"
                        type="text"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="6"
                        class="form-control shadow-none otp-input"
                        placeholder="6-digit code"
                        v-on:input="onOtpInput"
                      />
                    </div>
                    <button type="submit" class="btn btn-dark" :disabled="isLoading">
                      <span
                        v-if="isLoading"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span v-if="!isLoading">Verify code</span>
                    </button>
                    <div class="resend-row">
                      <button
                        type="button"
                        class="link-button"
                        :disabled="resendIn > 0 || isLoading"
                        v-on:click="sendOtp"
                      >
                        {{ resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code" }}
                      </button>
                      <button type="button" class="link-button" v-on:click="changePhone">
                        Change number
                      </button>
                    </div>
                  </Form>

                  <!-- Step 3: set the new password -->
                  <Form v-else class="form" @submit="submitNewPassword">
                    <div class="form-group">
                      <Field
                        id="newPassword"
                        v-model="newPassword"
                        :rules="{ required: ['Password'], min: [6] }"
                        type="password"
                        name="newPassword"
                        class="form-control shadow-none"
                        placeholder="New password"
                      />
                      <ErrorMessage name="newPassword" class="form-text text-danger" />
                    </div>
                    <div class="form-group">
                      <Field
                        id="confirmPassword"
                        v-model="confirmPassword"
                        :rules="{ required: ['Confirm Password'] }"
                        type="password"
                        name="confirmPassword"
                        class="form-control shadow-none"
                        placeholder="Confirm password"
                      />
                      <ErrorMessage name="confirmPassword" class="form-text text-danger" />
                    </div>
                    <button type="submit" class="btn btn-dark" :disabled="isLoading">
                      <span
                        v-if="isLoading"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span v-if="!isLoading">Reset password</span>
                    </button>
                  </Form>

                  <!-- Invisible reCAPTCHA mount point for Firebase phone auth -->
                  <div id="recaptcha-container"></div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>
      <span class="text-center copyright auth-footer">
        &copy; Copyright {{ new Date().getFullYear() }} Abol Verify
      </span>
    </div>
  </div>
</template>
<script>
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { email } from "@vee-validate/rules";
import { LOGIN_ROUTE } from "@/router/routes";
import Auth from "@/services/auth";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { COUNTRY_CODES, normalizePhoneNumber } from "@/utils/phone";

defineRule("email", email);
defineRule("required", (value, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});
defineRule("min", (value, [limit]) => {
  if (value && value.length < Number(limit)) {
    return `Must be at least ${limit} characters`;
  }
  return true;
});

const FIREBASE_ERRORS = {
  "auth/invalid-phone-number": "That phone number looks invalid. Please check and try again.",
  "auth/missing-phone-number": "Please enter your phone number.",
  "auth/quota-exceeded": "Too many attempts right now. Please try again later.",
  "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
  "auth/invalid-verification-code": "That code is incorrect. Please try again.",
  "auth/code-expired": "That code has expired. Please request a new one.",
  "auth/captcha-check-failed": "Verification failed. Please try again.",
  "auth/user-disabled": "This phone number has been disabled.",
};

const RESEND_SECONDS = 45;

export default {
  title: "Forgot Password",
  components: {
    Field,
    Form,
    ErrorMessage,
  },
  data() {
    return {
      LOGIN_ROUTE,
      isLoading: false,
      done: false,
      doneMode: "email",
      errorMessage: "",

      // Email flow
      submittedEmail: "",
      forgotObj: {
        email: null,
      },

      // Phone flow
      method: "email",
      phoneStep: "request", // request -> verify -> reset
      countryCodes: COUNTRY_CODES,
      selectedCountryCode: "+251",
      phoneLocalNumber: "",
      phoneError: "",
      verifiedPhoneNumber: "",
      otpCode: "",
      newPassword: "",
      confirmPassword: "",
      resendIn: 0,
    };
  },
  computed: {
    // Mirrors isFirebaseConfigured() from @/utils/firebase without importing the
    // Firebase SDK (so it isn't bundled into this page just to render the toggle).
    phoneResetEnabled() {
      return Boolean(
        process.env.VUE_APP_FIREBASE_API_KEY &&
        process.env.VUE_APP_FIREBASE_AUTH_DOMAIN &&
        process.env.VUE_APP_FIREBASE_PROJECT_ID &&
        process.env.VUE_APP_FIREBASE_APP_ID,
      );
    },
    headerTitle() {
      if (this.method === "phone") {
        if (this.phoneStep === "verify") return "Enter the code";
        if (this.phoneStep === "reset") return "Set a new password";
      }
      return "Forgot password";
    },
    headerSubtitle() {
      if (this.method === "phone") {
        if (this.phoneStep === "verify") {
          return `We sent a 6-digit code to ${this.verifiedPhoneNumber}.`;
        }
        if (this.phoneStep === "reset") {
          return "Choose a new password for your account.";
        }
        return "Verify your phone number to reset your password.";
      }
      return "Enter your email and we'll send you a link to reset your password.";
    },
  },
  methods: {
    setMethod(method) {
      if (this.method === method) return;
      this.method = method;
      this.errorMessage = "";
      this.phoneError = "";
      // Reset the phone flow so switching back always starts clean.
      this.phoneStep = "request";
      this.otpCode = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.firebaseIdToken = "";
      this.confirmationResult = null;
      this.stopResendCountdown();
      this.clearRecaptcha();
    },
    onPhoneInput() {
      if (this.phoneError) this.phoneError = "";
      if (this.errorMessage) this.errorMessage = "";
    },
    onOtpInput() {
      // Keep digits only and re-clear stale errors as the user types.
      this.otpCode = (this.otpCode || "").replace(/\D/g, "");
      if (this.errorMessage) this.errorMessage = "";
    },

    // ---- Email flow -------------------------------------------------------
    async submitEmail() {
      this.errorMessage = "";
      this.isLoading = true;
      try {
        const res = await Auth.forgotPassword({ email: this.forgotObj.email });
        if (res && res.data && res.data.status) {
          this.submittedEmail = this.forgotObj.email;
          this.doneMode = "email";
          this.done = true;
          this.$toast.success(res.data.msg || "Reset link sent.", TOAST_GLOBAL_CONFIG);
        } else {
          this.errorMessage =
            this.$store.getters.errorMsg ||
            (res && res.data && res.data.msg) ||
            "Could not send the reset link. Please try again.";
        }
      } catch (e) {
        this.errorMessage = "Could not send the reset link. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    // ---- Phone flow -------------------------------------------------------
    async sendOtp() {
      this.errorMessage = "";
      // Firebase env values aren't filled in yet — keep the flow visible but
      // tell the user clearly instead of throwing a cryptic SDK error.
      if (!this.phoneResetEnabled) {
        this.errorMessage =
          "Phone verification isn't set up yet. Please use email reset for now.";
        return;
      }
      const phoneNumber = normalizePhoneNumber(this.selectedCountryCode, this.phoneLocalNumber);
      if (phoneNumber.replace(/\D/g, "").length < 8) {
        this.phoneError = "Enter a valid phone number.";
        return;
      }
      this.phoneError = "";
      this.isLoading = true;
      try {
        const fb = await import("@/utils/firebase");
        const auth = fb.getFirebaseAuth();

        // Recreate the verifier each send so a previous attempt's widget can't
        // leave it in a "already rendered" state.
        this.clearRecaptcha();
        this.recaptchaVerifier = new fb.RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
        });

        this.confirmationResult = await fb.signInWithPhoneNumber(
          auth,
          phoneNumber,
          this.recaptchaVerifier,
        );
        this.verifiedPhoneNumber = phoneNumber;
        this.otpCode = "";
        this.phoneStep = "verify";
        this.startResendCountdown();
        this.$toast.success("Verification code sent.", TOAST_GLOBAL_CONFIG);
      } catch (e) {
        this.errorMessage = this.firebaseErrorMessage(e);
        this.clearRecaptcha();
      } finally {
        this.isLoading = false;
      }
    },
    async verifyOtp() {
      this.errorMessage = "";
      const code = (this.otpCode || "").trim();
      if (!/^\d{6}$/.test(code)) {
        this.errorMessage = "Enter the 6-digit code we sent you.";
        return;
      }
      if (!this.confirmationResult) {
        this.errorMessage = "Your session expired. Please request a new code.";
        this.phoneStep = "request";
        return;
      }
      this.isLoading = true;
      try {
        const credential = await this.confirmationResult.confirm(code);
        this.firebaseIdToken = await credential.user.getIdToken();
        this.stopResendCountdown();
        this.phoneStep = "reset";
      } catch (e) {
        this.errorMessage = this.firebaseErrorMessage(e);
      } finally {
        this.isLoading = false;
      }
    },
    async submitNewPassword() {
      this.errorMessage = "";
      if (!this.newPassword || this.newPassword.length < 6) {
        this.errorMessage = "Password must be at least 6 characters.";
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.errorMessage = "Passwords do not match.";
        return;
      }
      if (!this.firebaseIdToken) {
        this.errorMessage = "Your verification expired. Please verify your number again.";
        this.phoneStep = "request";
        return;
      }
      this.isLoading = true;
      try {
        const res = await Auth.resetPasswordWithPhone({
          firebaseIdToken: this.firebaseIdToken,
          newPassword: this.newPassword,
        });
        if (res && res.data && res.data.status) {
          this.doneMode = "phone";
          this.done = true;
          this.clearRecaptcha();
          this.$toast.success(
            res.data.msg || "Password reset successfully.",
            TOAST_GLOBAL_CONFIG,
          );
        } else {
          this.errorMessage =
            this.$store.getters.errorMsg ||
            (res && res.data && res.data.msg) ||
            "Could not reset your password. Please try again.";
        }
      } catch (e) {
        this.errorMessage = "Could not reset your password. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    changePhone() {
      this.phoneStep = "request";
      this.otpCode = "";
      this.confirmationResult = null;
      this.errorMessage = "";
      this.stopResendCountdown();
      this.clearRecaptcha();
    },
    firebaseErrorMessage(e) {
      const code = e && e.code ? e.code : "";
      return FIREBASE_ERRORS[code] || "Something went wrong. Please try again.";
    },
    clearRecaptcha() {
      if (this.recaptchaVerifier) {
        try {
          this.recaptchaVerifier.clear();
        } catch (e) {
          /* ignore — widget may already be gone */
        }
        this.recaptchaVerifier = null;
      }
    },
    startResendCountdown() {
      this.stopResendCountdown();
      this.resendIn = RESEND_SECONDS;
      this.resendTimer = setInterval(() => {
        this.resendIn -= 1;
        if (this.resendIn <= 0) this.stopResendCountdown();
      }, 1000);
    },
    stopResendCountdown() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendIn = 0;
    },
  },
  created() {
    // Non-reactive Firebase handles (kept off `data()` so Vue doesn't proxy them).
    this.recaptchaVerifier = null;
    this.confirmationResult = null;
    this.firebaseIdToken = "";
    this.resendTimer = null;
  },
  beforeUnmount() {
    this.stopResendCountdown();
    this.clearRecaptcha();
  },
};
</script>
<style scoped>
.auth-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 32px;
  height: 32px;
  border-radius: var(--ehs-radius-sm);
  background: linear-gradient(135deg, var(--ehs-color-primary) 0%, #bcdc44 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  letter-spacing: -0.05em;
  box-shadow: 0 4px 12px rgba(115, 196, 29, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.brand-bracket {
  font-family: var(--ehs-font-mono);
  font-size: 1rem;
  font-weight: 600;
  opacity: 0.9;
  line-height: 1;
}

.brand-glyph {
  display: inline-block;
  width: 5px;
  height: 5px;
  background-color: #ffffff;
  border-radius: 1px;
  margin: 0 1px;
}

.brand-name {
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.auth-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin-top: 1.5rem;
  padding: 4px;
  background: var(--ehs-color-canvas-strong);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-md);
}

.auth-toggle button {
  border: 0;
  background: transparent;
  padding: 8px 14px;
  border-radius: var(--ehs-radius-sm);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.auth-toggle button.is-active {
  background: var(--ehs-color-surface);
  color: var(--ehs-color-primary);
  box-shadow: var(--ehs-shadow-xs);
}

.form {
  margin-top: 1.25rem;
}

.sub-title {
  margin-top: 0.5rem;
}

.phone-input-row {
  display: grid;
  grid-template-columns: minmax(118px, 0.4fr) minmax(0, 1fr);
  gap: 12px;
}

.country-code-select {
  padding-left: 10px !important;
  padding-right: 10px !important;
}

.otp-input {
  letter-spacing: 0.4em;
  text-align: center;
  font-family: var(--ehs-font-mono);
  font-size: 1.1rem;
}

.resend-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}

.link-button {
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ehs-color-primary);
  cursor: pointer;
}

.link-button:disabled {
  color: var(--ehs-color-soft);
  cursor: default;
}

.auth-switch-link {
  margin-top: 16px;
  margin-bottom: 0;
}

.state-block {
  padding: 12px 0;
}

@media (max-width: 991.98px) {
  .auth-card {
    padding: 22px 18px !important;
  }

  .auth-center {
    transform: none;
    padding: 24px 0 80px;
  }

  .auth-title {
    font-size: 1.65rem !important;
  }
}
</style>
